import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, CloseIcon } from "../../../../components/restaurant-schema/schemaIcons";
import type { PickTable } from "../../logic/newReservation";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import { TABLES_SECTION_ID } from "../../hooks/useSaveReservation";
import { HandPointerIcon, SectionLabel, SuggestTableButton } from "./formPrimitives";

/**
 * „МАСИ *": „☆ Предложи маса", празно поле с подсказка или избраните като
 * чипове „Маса 2 · 6 места ×", под тях местата спрямо гостите (зелено/червено,
 * не блокира) и червен ред за избрана маса, заета в този час (спира „Създай"/
 * „Запази" — сметка, отворена в Маркиране точно преди часа, или застъпване). Докато
 * заетостта на деня се зарежда или не се е заредила — ред за това (с „Опитай
 * пак" при грешка), защото изборът и „Създай" чакат. На телефон (`picker`)
 * масите се избират тук — регион, после плочка — вместо на схемата; тогава
 * без подсказката и чиповете (избраните се виждат в плочките).
 */
const TablesSection = ({
  selectedTables,
  busySelectedTables,
  guests,
  seats,
  hasEnoughSeats,
  dayState,
  onRetryDay,
  onSuggest,
  onRemove,
  picker = null,
}: {
  selectedTables: PickTable[];
  busySelectedTables: PickTable[];
  guests: number;
  seats: number;
  hasEnoughSeats: boolean;
  // Заетостта на деня: заредена / зарежда се / грешка.
  dayState: "ready" | "loading" | "error";
  onRetryDay: () => void;
  onSuggest: () => void;
  onRemove: (tableId: number) => void;
  // Изборът на телефон (MobileTablePicker); null — масите се избират на схемата.
  picker?: ReactNode;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();

  return (
    <div id={TABLES_SECTION_ID} className="flex flex-col gap-2">
      {/* На телефон етикетите (Регион, Маси + „Предложи маса") са в самия избор. */}
      {picker === null && (
        <div className="flex items-center justify-between gap-2">
          <SectionLabel required>{t("reservations.form.tables")}</SectionLabel>
          <SuggestTableButton onClick={onSuggest} />
        </div>
      )}

      {dayState === "loading" && (
        <div className="text-[12.5px] font-medium text-muted">{t("reservations.form.dayLoading")}</div>
      )}
      {dayState === "error" && (
        <div className="flex items-center justify-between gap-2 rounded-[8px] bg-danger-tint px-2.5 py-2 text-[12.5px] font-semibold leading-[1.4] text-danger-ink">
          {t("reservations.form.dayError")}
          <button
            type="button"
            onClick={onRetryDay}
            className="shrink-0 cursor-pointer rounded-md border border-danger bg-surface px-2 py-1 text-[12px] font-bold text-danger-ink transition-colors duration-150 hover:bg-fill"
          >
            {t("reservations.form.retry")}
          </button>
        </div>
      )}

      {picker}

      {selectedTables.length === 0 ? (
        picker === null && (
          <div className="flex items-center gap-3 rounded-[10px] border border-dashed border-shade-4 px-3.5 py-3 text-[12.5px] font-medium leading-[1.45] text-body">
            <HandPointerIcon className="size-5 shrink-0 text-brand-deep-ink" />
            {t("reservations.form.tablesEmpty")}
          </div>
        )
      ) : (
        <>
          {picker === null && (
            <div className="flex flex-wrap gap-1.5">
              {selectedTables.map((table) => {
                const busy = busySelectedTables.includes(table);
                return (
                  <span
                    key={table.id}
                    className={`flex h-8 items-center gap-1.5 rounded-[8px] border pl-2.5 pr-1 text-[12.5px] font-bold ${
                      busy
                        ? "border-danger bg-danger-tint text-danger-ink"
                        : "border-brand bg-brand-tint text-brand-deep-ink"
                    }`}
                  >
                    {t("reservations.form.tableChip", { name: table.name, seats: format.seats(table.seats) })}
                    <button
                      type="button"
                      onClick={() => onRemove(table.id)}
                      aria-label={t("reservations.form.removeTable", { name: table.name })}
                      className="flex size-6 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-current transition-colors duration-150 hover:bg-shade-1"
                    >
                      <CloseIcon className="size-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <div
            className={`flex items-center gap-1.5 text-[12.5px] font-semibold ${
              hasEnoughSeats ? "text-schema-fits-ink" : "text-danger-ink"
            }`}
          >
            {hasEnoughSeats ? (
              <>
                <CheckIcon className="size-3.5" />
                {t("reservations.form.seatsEnough", { seats: format.seats(seats), guests: format.guests(guests) })}
              </>
            ) : (
              t("reservations.form.seatsShort", { seats, guests: format.guests(guests) })
            )}
          </div>
          {busySelectedTables.length > 0 && (
            <div className="text-[12.5px] font-semibold text-danger-ink">
              {t("reservations.form.tablesBusy", {
                names: busySelectedTables.map((table) => format.tableName(table.name)).join(", "),
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TablesSection;
